import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Tooltip,
  Paper,
} from '@mui/material';
import { Course, FilterState, CustomTimeBlock, DAYS, DAY_LABELS, PX_PER_HOUR, DAY_START_MIN, DAY_END_MIN } from '../types';
import { customBlocksToCourses, conflictingCrns } from '../utils/conflicts';
import { matchesNeededOrSubjectFilters } from '../utils/catalogClasses';

const TIME_COL_WIDTH = 60;
const VISIBLE_DAYS = 2;
const NEXT_DAY_PEEK = 32;

interface ScheduleGridProps {
  courses: Course[];
  mySchedule: Course[];
  customBlocks?: CustomTimeBlock[];
  filters: FilterState;
  onAddCourse: (crn: string) => void;
  onRemoveCourse: (crn: string) => void;
  onEditCustomBlock?: (blockId: string) => void;
  isMySchedule?: boolean;
  courseOpacity?: number;
  sharedTimeRange?: { startMin: number; endMin: number };
  weekView?: 'full' | 'twoDay';
}

const ScheduleGrid: React.FC<ScheduleGridProps> = ({
  courses,
  mySchedule,
  customBlocks = [],
  filters,
  onAddCourse,
  onRemoveCourse,
  onEditCustomBlock,
  isMySchedule = false,
  courseOpacity = 0.6,
  sharedTimeRange,
  weekView = 'full',
}) => {
  // State for cycling through overlapping courses
  const [overlappingGroups, setOverlappingGroups] = useState<{ [key: string]: { courses: Course[], currentIndex: number } }>({});
  
  // Mobile tooltip state
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const twoDayView = weekView === 'twoDay';
  const twoDayCol = `calc((100cqi - ${TIME_COL_WIDTH}px - ${NEXT_DAY_PEEK}px) / ${VISIBLE_DAYS})`;

  // Convert custom blocks to course-like objects for display
  const customBlockCourses = useMemo(() => customBlocksToCourses(customBlocks), [customBlocks]);

  const conflictCrnSet = useMemo(() => {
    if (isMySchedule) return new Set<string>();
    return conflictingCrns(courses, [...mySchedule, ...customBlockCourses]);
  }, [isMySchedule, courses, mySchedule, customBlockCourses]);

  // Enrollment is only listed on a section's first meeting row; extra meetings inherit it.
  const enrollmentByCrn = useMemo(() => {
    const map = new Map<string, Course>();
    for (const c of courses) {
      if (!c.isLabSection && !map.has(c.CRN)) map.set(c.CRN, c);
    }
    return map;
  }, [courses]);

  // Mobile detection
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle tooltip open/close for mobile
  const handleTooltipToggle = useCallback((courseKey: string) => {
    if (isMobile) {
      setOpenTooltip(prev => prev === courseKey ? null : courseKey);
    }
  }, [isMobile]);

  // Close tooltip when clicking outside (mobile only)
  React.useEffect(() => {
    if (!isMobile || !openTooltip) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Don't close if clicking on a tooltip or course card
      if (!target.closest('.MuiTooltip-popper') && !target.closest('.course-slot')) {
        setOpenTooltip(null);
      }
    };

    const handleScroll = () => {
      // Don't close tooltip on scroll - let users scroll while tooltip is open
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isMobile, openTooltip]);
  
  const filteredCourses = useMemo(() => {
    // If this is "My Schedule", show all courses plus custom blocks
    if (isMySchedule) {
      return [...courses, ...customBlockCourses];
    }
    
    // Debug logging
    console.log('Filtering courses with:', {
      showFullClasses: filters.showFullClasses,
      showFullWaitlist: filters.showFullWaitlist,
      totalCourses: courses.length
    });
    
    // For available courses, apply all filters
    // First, find all lecture sections that pass the filter
    const lectureSections = courses.filter(course => {
      if (course.isLabSection) return false; // Skip lab sections for now
      if (mySchedule.find(m => m.CRN === course.CRN && !m.unavailable)) return false; // Don't show courses already in my schedule
      
      const classOk = matchesNeededOrSubjectFilters(course, filters);
      const instrOk = filters.instructorAllow.size === 0 || filters.instructorAllow.has(course.Instructor);
      const campusOk = filters.campusAllow.size === 0 || filters.campusAllow.has(course.Campus);
      
      // Full class filter - skip for lab sections (they don't have enrollment data)
      const isFull = course.Remaining <= 0 && course.Capacity > 0;
      const fullOk = filters.showFullClasses || !isFull;
      
      // Full waitlist filter - skip for lab sections (they don't have enrollment data)
      const isWaitlistFull = course.WaitRem <= 0 && course.WaitCap > 0;
      const waitlistOk = filters.showFullWaitlist || !isWaitlistFull;
      const conflictOk = filters.showConflicts || !conflictCrnSet.has(course.CRN);
      
      return classOk && instrOk && campusOk && fullOk && waitlistOk && conflictOk;
    });
    
    // Get CRNs of lecture sections that passed the filter
    const allowedCRNs = new Set(lectureSections.map(course => course.CRN));
    
    console.log(`First pass: ${lectureSections.length} lecture sections passed the filter`);
    
    // Now filter all courses (including lab sections) based on whether their CRN is allowed
    const filtered = courses.filter(course => {
      // Don't show courses already in my schedule
      if (mySchedule.find(m => m.CRN === course.CRN && !m.unavailable)) return false;
      
      // If this is a lab section, check if its lecture section passed the filter
      if (course.isLabSection) {
        return allowedCRNs.has(course.CRN);
      }
      
      // For lecture sections, use the original filtering logic
      const classOk = matchesNeededOrSubjectFilters(course, filters);
      const instrOk = filters.instructorAllow.size === 0 || filters.instructorAllow.has(course.Instructor);
      
      // Campus filtering: if no campus filters are selected, show all; otherwise check if course campus is in the selected set
      const campusOk = filters.campusAllow.size === 0 || filters.campusAllow.has(course.Campus);
      
      // Full class filter - skip for lab sections (they don't have enrollment data)
      const isFull = course.Remaining <= 0 && course.Capacity > 0;
      const fullOk = filters.showFullClasses || !isFull;
      
      // Full waitlist filter - skip for lab sections (they don't have enrollment data)
      const isWaitlistFull = course.WaitRem <= 0 && course.WaitCap > 0;
      const waitlistOk = filters.showFullWaitlist || !isWaitlistFull;
      
      // Debug logging for full classes
      if (isFull && !filters.showFullClasses) {
        console.log(`Filtering out full class: ${course.Subject} ${course.Course} - Remaining: ${course.Remaining}, Capacity: ${course.Capacity}`);
      }
      
      // Debug logging for full waitlists
      if (isWaitlistFull && !filters.showFullWaitlist) {
        console.log(`Filtering out full waitlist: ${course.Subject} ${course.Course} - WaitRem: ${course.WaitRem}, WaitCap: ${course.WaitCap}`);
      }

      const conflictOk = filters.showConflicts || !conflictCrnSet.has(course.CRN);
      
      return classOk && instrOk && campusOk && fullOk && waitlistOk && conflictOk;
    });
    
    console.log(`Final result: ${filtered.length} courses after filtering`);
    return filtered;
  }, [courses, customBlockCourses, mySchedule, filters, isMySchedule, conflictCrnSet]);

  // Use shared time range if provided, otherwise calculate from filtered courses
  const timeRange = useMemo(() => {
    if (sharedTimeRange) {
      return sharedTimeRange;
    }
    
    if (filteredCourses.length === 0) {
      return { startMin: DAY_START_MIN, endMin: DAY_END_MIN };
    }
    
    const allStartMins = filteredCourses.map(course => course.StartMin);
    const allEndMins = filteredCourses.map(course => course.EndMin);
    
    const earliestStart = Math.min(...allStartMins);
    const latestEnd = Math.max(...allEndMins);
    
    // Round to nearest hour for cleaner display
    const startMin = Math.floor(earliestStart / 60) * 60;
    const endMin = Math.ceil(latestEnd / 60) * 60;
    
    // Ensure minimum range of 8 AM to 6 PM
    const minStart = 8 * 60; // 8 AM
    const minEnd = 18 * 60; // 6 PM
    
    return {
      startMin: Math.min(startMin, minStart),
      endMin: Math.max(endMin, minEnd)
    };
  }, [sharedTimeRange, filteredCourses]);

  const dayColumns = useMemo(() => {
    const columns: { [key: string]: Course[] } = {};
    DAYS.forEach(day => {
      columns[day] = filteredCourses.filter(course => course.Days.includes(day));
    });
    
    return columns;
  }, [filteredCourses]);

  // Function to detect overlapping courses for a given day
  const getOverlappingGroups = useCallback((dayCourses: Course[]) => {
    const groups: { [key: string]: Course[] } = {};
    
    dayCourses.forEach(course => {
      // Find all courses that overlap with this course
      const overlappingCourses = dayCourses.filter(otherCourse => {
        if (otherCourse.CRN === course.CRN) return false; // Don't include self
        
        // Check if courses overlap in time
        const courseStart = course.StartMin;
        const courseEnd = course.EndMin;
        const otherStart = otherCourse.StartMin;
        const otherEnd = otherCourse.EndMin;
        
        // Two courses overlap if one starts before the other ends and vice versa
        return (courseStart < otherEnd && courseEnd > otherStart);
      });
      
      // If there are overlapping courses, create a group
      if (overlappingCourses.length > 0) {
        const timeKey = `${course.StartMin}-${course.EndMin}`;
        if (!groups[timeKey]) {
          // Include the current course and all overlapping courses
          groups[timeKey] = [course, ...overlappingCourses];
        }
      }
    });
    
    return groups;
  }, []);


  // Function to cycle through overlapping courses
  const cycleOverlappingCourses = useCallback((day: string, timeKey: string, direction: 'next' | 'prev' | 'set', targetIndex?: number) => {
    setOverlappingGroups(prev => {
      const currentGroup = prev[`${day}-${timeKey}`];
      if (!currentGroup) return prev;
      
      if (direction === 'set' && targetIndex !== undefined) {
        // When a course is selected, bring all cards with the same CRN to the top
        const selectedCourse = currentGroup.courses[targetIndex];
        if (selectedCourse) {
          // Find all overlapping groups that contain courses with the same CRN
          const updatedGroups = { ...prev };
          
          Object.keys(updatedGroups).forEach(groupKey => {
            const group = updatedGroups[groupKey];
            const sameCRNIndex = group.courses.findIndex(course => course.CRN === selectedCourse.CRN);
            
            if (sameCRNIndex !== -1) {
              // Move the course with the same CRN to the top (index 0)
              const reorderedCourses = [...group.courses];
              const courseToMove = reorderedCourses.splice(sameCRNIndex, 1)[0];
              reorderedCourses.unshift(courseToMove);
              
              updatedGroups[groupKey] = {
                ...group,
                courses: reorderedCourses,
                currentIndex: 0 // The moved course is now at index 0
              };
            }
          });
          
          return updatedGroups;
        }
      } else {
        // For next/prev directions, just change the current index
        let newIndex;
        if (direction === 'next') {
          newIndex = (currentGroup.currentIndex + 1) % currentGroup.courses.length;
        } else {
          newIndex = (currentGroup.currentIndex - 1 + currentGroup.courses.length) % currentGroup.courses.length;
        }
        
        return {
          ...prev,
          [`${day}-${timeKey}`]: {
            ...currentGroup,
            currentIndex: newIndex
          }
        };
      }
      
      return prev;
    });
  }, []);


  // Initialize overlapping groups when dayColumns change
  React.useEffect(() => {
    const newOverlappingGroups: { [key: string]: { courses: Course[], currentIndex: number } } = {};
    
    DAYS.forEach(day => {
      const dayCourses = dayColumns[day] || [];
      const overlapping = getOverlappingGroups(dayCourses);
      
      Object.entries(overlapping).forEach(([timeKey, courses]) => {
        const groupKey = `${day}-${timeKey}`;
        newOverlappingGroups[groupKey] = {
          courses,
          currentIndex: 0
        };
      });
    });
    
    setOverlappingGroups(newOverlappingGroups);
  }, [dayColumns, getOverlappingGroups]);

  const renderTimeColumn = () => {
    const timeSlots = [];
    for (let m = timeRange.startMin; m <= timeRange.endMin; m += 60) {
      const hh = Math.floor(m / 60);
      const ampm = hh >= 12 ? "PM" : "AM";
      const h12 = ((hh + 11) % 12) + 1;
      
      timeSlots.push(
        <Box
          key={m}
          sx={{
            height: PX_PER_HOUR,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            color: 'text.secondary',
            borderBottom: (theme) => theme.palette.mode === 'dark' 
              ? '1px dashed #2a3c55' 
              : '1px dashed #d1d5db',
            padding: '2px 6px',
            boxSizing: 'border-box',
            background: (theme) => theme.palette.mode === 'dark' ? '#0f1722' : '#f0f4f8',
            width: '100%',
          }}
        >
          {h12}:00 {ampm}
        </Box>
      );
    }
    return timeSlots;
  };

  const renderCourseSlot = (course: Course, day: string, index: number = 0) => {
    const top = ((course.StartMin - timeRange.startMin) / 60) * PX_PER_HOUR;
    const height = ((course.EndMin - course.StartMin) / 60) * PX_PER_HOUR;
    
    // Check if this course is part of an overlapping group
    const timeKey = `${course.StartMin}-${course.EndMin}`;
    const groupKey = `${day}-${timeKey}`;
    const overlappingGroup = overlappingGroups[groupKey];
    const isOverlapping = overlappingGroup && overlappingGroup.courses.length > 1;
    
    // For overlapping courses, show all courses in the group
    // The currentIndex is used for which course appears on top in the tooltip
    // but all courses should be visible as stacked cards
    
    const courseKey = `${course.CRN}-${day}-${course.StartMin}-${course.EndMin}`;
    const isTooltipOpen = isMobile ? openTooltip === courseKey : undefined;

    // Determine which course to show in the tooltip
    const displayCourse = isOverlapping && overlappingGroup 
      ? overlappingGroup.courses[overlappingGroup.currentIndex] 
      : course;
    const enrollment = displayCourse.isLabSection
      ? enrollmentByCrn.get(displayCourse.CRN) ?? displayCourse
      : displayCourse;
    const isConflicted = !isMySchedule && conflictCrnSet.has(course.CRN);

    return (
      <Tooltip
        key={`${course.CRN}-${day}-${course.StartMin}-${course.EndMin}-${course.Location}-${course.Instructor || 'TBA'}-${course.isLabSection ? 'lab' : 'lecture'}-${index}-${overlappingGroup ? overlappingGroup.currentIndex : 0}`}
        title={
          <Box>
            {displayCourse.isCustomBlock ? (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {displayCourse.Title}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Custom Time Block
                </Typography>
                {displayCourse.customCrn && (
                  <Typography variant="body2">
                    CRN: {displayCourse.customCrn}
                  </Typography>
                )}
                {displayCourse.Instructor && (
                  <Typography variant="body2">
                    {displayCourse.Instructor}
                  </Typography>
                )}
                {(displayCourse.Location || displayCourse.Campus) && (
                  <Typography variant="body2">
                    {[displayCourse.Location, displayCourse.Campus].filter(Boolean).join(' • ')}
                  </Typography>
                )}
                {displayCourse.customField && (
                  <Typography variant="body2">
                    {displayCourse.customField}
                  </Typography>
                )}
                <Typography variant="body2">
                  {displayCourse.Days} {displayCourse.DispTime}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {displayCourse.Subject} {displayCourse.Course}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {displayCourse.Title}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  CRN: {displayCourse.CRN} • {displayCourse.Instructor || 'TBA'}
                </Typography>
                <Typography variant="body2">
                  {displayCourse.Location} • {displayCourse.Campus}
                </Typography>
                <Typography variant="body2">
                  {displayCourse.Days} {displayCourse.DispTime}
                </Typography>
                {(enrollment.Capacity > 0 || enrollment.Actual > 0) && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Seats: {enrollment.Actual}/{enrollment.Capacity} ({enrollment.Remaining} open)
                  </Typography>
                )}
                {(enrollment.WaitCap > 0 || enrollment.WaitAct > 0) && (
                  <Typography variant="body2">
                    Waitlist: {enrollment.WaitAct}/{enrollment.WaitCap} ({enrollment.WaitRem} open)
                  </Typography>
                )}
                {isConflicted && (
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 'bold', color: '#fbbf24' }}>
                    Conflicts with My Schedule
                  </Typography>
                )}
              </>
            )}
            {isOverlapping && (
              <>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'primary.main' }}>
                  {overlappingGroup.courses.length} courses at this time:
                </Typography>
                {overlappingGroup.courses.map((overlappingCourse, idx) => (
                  <Box
                    key={`${overlappingCourse.CRN}-${idx}-${day}-${timeKey}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      cycleOverlappingCourses(day, timeKey, 'set', idx);
                    }}
                    sx={{
                      mt: 0.5,
                      ml: 1,
                      p: 0.5,
                      borderRadius: 1,
                      cursor: 'pointer',
                      backgroundColor: idx === overlappingGroup.currentIndex ? 'primary.main' : 'transparent',
                      color: idx === overlappingGroup.currentIndex ? 'white' : 'text.secondary',
                      fontWeight: idx === overlappingGroup.currentIndex ? 'bold' : 'normal',
                      '&:hover': {
                        backgroundColor: idx === overlappingGroup.currentIndex ? 'primary.dark' : 'action.hover',
                        color: idx === overlappingGroup.currentIndex ? 'white' : 'text.primary',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {idx + 1}. {overlappingCourse.isCustomBlock ? overlappingCourse.Title : `${overlappingCourse.Subject} ${overlappingCourse.Course}`} - {overlappingCourse.isCustomBlock ? (overlappingCourse.Instructor || 'Custom Block') : (overlappingCourse.Instructor || 'TBA')}
                  </Box>
                ))}
                <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                  Click a course to bring it to the top
                </Typography>
              </>
            )}
          </Box>
        }
        arrow
        open={isTooltipOpen}
        enterDelay={isMobile ? 0 : 400}
        leaveDelay={isMobile ? 0 : 400}
        enterNextDelay={isMobile ? 0 : 400}
        disableHoverListener={isMobile}
        disableTouchListener={false}
        PopperProps={{
          style: { zIndex: 9999 },
        }}
      >
        <Paper
          className="course-slot"
          elevation={0}
          onClick={() => isMobile && handleTooltipToggle(courseKey)}
          sx={{
            position: 'absolute',
            left: '6px',
            right: '6px',
            top: Math.max(0, top),
            height: Math.max(18, height),
            background: (() => {
              // Handle both HSL and hex colors with opacity applied only to background
              if (course.__color.startsWith('hsl(')) {
                // HSL color - convert to HSLA with opacity
                return course.__color.replace(/hsl\(([^)]+)\)/, (match, content) => {
                  return `hsla(${content}, ${courseOpacity})`;
                });
              } else if (course.__color.startsWith('#')) {
                // Hex color - convert to rgba with opacity
                const hex = course.__color.replace('#', '');
                const r = parseInt(hex.substr(0, 2), 16);
                const g = parseInt(hex.substr(2, 2), 16);
                const b = parseInt(hex.substr(4, 2), 16);
                return `rgba(${r}, ${g}, ${b}, ${courseOpacity})`;
              } else {
                // Fallback - return original color
                return course.__color;
              }
            })(),
            border: `1px solid ${course.__color}`,
            borderRadius: '8px',
            padding: '6px 8px',
            color: '#ffffff',
            boxShadow: isOverlapping 
              ? '0 4px 12px rgba(0,0,0,0.35), 0 0 0 2px rgba(255,255,255,0.4)' 
              : '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: isOverlapping ? (overlappingGroup.currentIndex === overlappingGroup.courses.findIndex(c => c.CRN === course.CRN) ? 4 : 3) : 2, // Selected course on top
            boxSizing: 'border-box',
            overflow: 'hidden',
            outline: course.unavailable
              ? '2px dashed #94a3b8'
              : `2px solid ${isConflicted ? '#94a3b8' : course.__color}`,
            outlineOffset: '-2px',
            filter: isConflicted || course.unavailable ? 'grayscale(1)' : 'none',
            opacity: isConflicted ? 0.55 : course.unavailable ? 0.7 : 1,
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
            },
            ...(isMobile && {
              cursor: 'pointer',
              '&:active': {
                transform: 'scale(0.98)',
              },
            }),
          }}
        >
          {/* Overlapping indicator */}
          {isOverlapping && (
            <Box sx={{
              position: 'absolute',
              top: '3px',
              right: '3px',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.9)',
              border: '2px solid rgba(0,0,0,0.3)',
              zIndex: 4,
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          )}
          {isConflicted && (
            <Box sx={{
              position: 'absolute',
              top: '3px',
              left: '3px',
              zIndex: 4,
              background: 'rgba(15, 23, 42, 0.75)',
              color: '#e2e8f0',
              fontSize: '8px',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              padding: '1px 4px',
              borderRadius: '3px',
            }}>
              Conflict
            </Box>
          )}
          
          <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {/* Course Title - or Custom Block Title */}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 'bold',
                fontSize: course.isCustomBlock ? '11px' : '12px',
                display: 'block',
                lineHeight: 1.2,
                marginBottom: '2px',
              }}
            >
              {course.isCustomBlock ? course.Title : `${course.Subject} ${course.Course}`}
            </Typography>
            
            {/* Tags: Custom tag and/or CRN */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px',
                marginBottom: '2px',
              }}
            >
              {course.isCustomBlock && (
                <Box
                  sx={{
                    display: 'inline-block',
                    fontSize: '8px',
                    padding: '1px 4px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.1)',
                    alignSelf: 'flex-start',
                  }}
                >
                  Custom
                </Box>
              )}
              {(!course.isCustomBlock || course.customCrn) && (
                <Box
                  sx={{
                    display: 'inline-block',
                    fontSize: '8px',
                    padding: '1px 4px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.1)',
                    alignSelf: 'flex-start',
                  }}
                >
                  {course.isCustomBlock ? course.customCrn : course.CRN}
                </Box>
              )}
              {course.unavailable && (
                <Box
                  sx={{
                    display: 'inline-block',
                    fontSize: '8px',
                    fontWeight: 700,
                    padding: '1px 4px',
                    borderRadius: '6px',
                    background: 'rgba(15, 23, 42, 0.75)',
                    color: '#e2e8f0',
                    alignSelf: 'flex-start',
                  }}
                >
                  No longer listed
                </Box>
              )}
            </Box>
            
            {/* Time */}
            <Typography
              variant="caption"
              sx={{
                fontSize: '9px',
                display: 'block',
                color: '#e8f0fe',
                opacity: 0.95,
                lineHeight: 1.2,
                fontWeight: 500,
              }}
            >
              {course.DispTime}
            </Typography>
            
            {/* Instructor */}
            {(!course.isCustomBlock || course.Instructor) && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '8px',
                  display: 'block',
                  color: '#d7e3ff',
                  opacity: 0.9,
                  lineHeight: 1.2,
                  marginBottom: '2px',
                }}
              >
                {course.isCustomBlock ? course.Instructor : (course.Instructor || 'TBA')}
              </Typography>
            )}

            {/* Location - custom blocks only when filled */}
            {course.isCustomBlock && course.Location && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '8px',
                  display: 'block',
                  color: '#d7e3ff',
                  opacity: 0.9,
                  lineHeight: 1.2,
                  marginBottom: '2px',
                }}
              >
                {course.Location}
              </Typography>
            )}

            {/* Campus - custom blocks only when filled */}
            {course.isCustomBlock && course.Campus && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '8px',
                  display: 'block',
                  color: '#d7e3ff',
                  opacity: 0.9,
                  lineHeight: 1.2,
                  marginBottom: '2px',
                }}
              >
                {course.Campus}
              </Typography>
            )}

            {/* Custom freeform field - displayed last */}
            {course.isCustomBlock && course.customField && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '8px',
                  display: 'block',
                  color: '#d7e3ff',
                  opacity: 0.9,
                  lineHeight: 1.2,
                  marginBottom: '2px',
                }}
              >
                {course.customField}
              </Typography>
            )}
            
            {/* Overlapping Course Indicator */}
            {isOverlapping && (
              <Box sx={{
                position: 'absolute',
                bottom: '-2px',
                left: '-4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1px 0',
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '8px',
                    color: '#ffffff',
                    fontWeight: 600,
                    textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    padding: '1px 4px',
                    borderRadius: '3px',
                    border: '1px solid rgba(255,255,255,0.3)',
                  }}
                >
                  {overlappingGroup.currentIndex + 1} of {overlappingGroup.courses.length}
                </Typography>
              </Box>
            )}
            
            {/* Add/Remove Button - only for regular courses, not custom blocks */}
            {!course.isCustomBlock && (
              <Button
                size="small"
                variant="contained"
                sx={{
                  position: 'absolute',
                  bottom: '-2px',
                  right: '-4px',
                  minWidth: 'auto',
                  width: 'auto',
                  height: 'auto',
                  padding: '2px 6px',
                  fontSize: '9px',
                  background: isMySchedule ? '#dc3545' : (isConflicted ? '#64748b' : '#059669'),
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  '&:hover': {
                    background: isMySchedule ? '#c82333' : (isConflicted ? '#475569' : '#048a5a'),
                    transform: 'translateY(-1px)',
                    boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isMySchedule) {
                    onRemoveCourse(course.CRN);
                  } else {
                    onAddCourse(course.CRN);
                  }
                }}
              >
                {isMySchedule ? 'Remove' : 'Add'}
              </Button>
            )}

            {/* Edit Button - only for custom blocks */}
            {course.isCustomBlock && (
              <Button
                size="small"
                variant="contained"
                sx={{
                  position: 'absolute',
                  bottom: '3px',
                  right: '3px',
                  minWidth: 'auto',
                  width: 'auto',
                  height: 'auto',
                  padding: '2px 6px',
                  fontSize: '9px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  '&:hover': {
                    background: '#5a6268',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onEditCustomBlock) {
                    onEditCustomBlock(course.CRN);
                  }
                }}
              >
                Edit
              </Button>
            )}
          </Box>
        </Paper>
      </Tooltip>
    );
  };

  const renderDayColumn = (day: string) => {
    const dayCourses = dayColumns[day] || [];
    
    return (
      <Box
        key={day}
        sx={{
          position: 'relative',
          zIndex: 0,
          isolation: 'isolate',
          minHeight: ((timeRange.endMin - timeRange.startMin) / 60) * PX_PER_HOUR,
          background: (theme) => theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, rgba(32,48,69,0.25), rgba(32,48,69,0.18))'
            : 'linear-gradient(180deg, rgba(13, 27, 42, 0.06), rgba(13, 27, 42, 0.03))',
          borderLeft: (theme) => theme.palette.mode === 'dark' 
            ? '1px solid #223246' 
            : '1px solid #d0d7de',
          height: '100%',
        }}
      >
        {dayCourses.map((course, index) => renderCourseSlot(course, day, index))}
      </Box>
    );
  };

  // Check if no subjects are selected (only for available courses, not my schedule)
  const noClassesSelected = !isMySchedule
    && filters.neededCourses.size === 0
    && filters.subjectAllow.size === 0;

  if (noClassesSelected) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          minHeight: 200,
          textAlign: 'center',
          padding: '20px 20px 0 20px',
        }}
      >
        <Box sx={{
          background: (theme) => theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #1a2330 0%, #0f1622 100%)'
            : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          border: (theme) => theme.palette.mode === 'dark'
            ? '1px solid #2a3c55'
            : '1px solid #d1d5db',
          borderRadius: '12px',
          padding: '20px',
          maxWidth: '350px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          <Typography variant="h2" sx={{ mb: 1, fontSize: '36px' }}>
            🎯
          </Typography>
          <Typography variant="h6" sx={{ 
            mb: 1, 
            fontWeight: 'bold',
            color: 'text.primary',
            fontSize: '16px',
          }}>
            Choose Classes to View Sections
          </Typography>
          <Typography variant="body2" sx={{ 
            mb: 2,
            color: 'text.secondary',
            lineHeight: 1.5,
            fontSize: '13px',
          }}>
            Tell us which classes you need. We’ll show every available section on the calendar so you can build a schedule that fits.
          </Typography>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            alignItems: 'flex-start',
            textAlign: 'left',
          }}>
            <Typography 
              variant="body2" 
              component="div"
              sx={{ 
                color: 'text.secondary',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Box sx={{ 
                width: '4px', 
                height: '4px', 
                borderRadius: '50%', 
                backgroundColor: 'primary.main' 
              }} />
              Click Choose Classes to add MATH 010, CHEM 012, and the rest of your list
            </Typography>
            <Typography 
              variant="body2" 
              component="div"
              sx={{ 
                color: 'text.secondary',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Box sx={{ 
                width: '4px', 
                height: '4px', 
                borderRadius: '50%', 
                backgroundColor: 'secondary.main' 
              }} />
              Search for a class, then add the section times that work
            </Typography>
            <Typography 
              variant="body2" 
              component="div"
              sx={{ 
                color: 'text.secondary',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Box sx={{ 
                width: '4px', 
                height: '4px', 
                borderRadius: '50%', 
                backgroundColor: 'warning.main' 
              }} />
              Add courses to your schedule by clicking the "Add" button
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (filteredCourses.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 400,
          textAlign: 'center',
        }}
      >
        <Box>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {isMySchedule ? '📅' : '📚'}
          </Typography>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {isMySchedule ? 'Your schedule is empty' : 'No courses match current filters'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isMySchedule 
              ? 'Add courses from the available courses panel to build your schedule.'
              : 'Try adjusting your filters to see more courses.'
            }
          </Typography>
        </Box>
      </Box>
    );
  }

  const gridTemplateColumns = twoDayView
    ? `${TIME_COL_WIDTH}px repeat(${DAYS.length}, ${twoDayCol})`
    : `${TIME_COL_WIDTH}px repeat(${DAYS.length}, 1fr)`;

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      containerType: 'inline-size',
      borderRadius: '10px',
      overflow: 'hidden',
      border: (theme) => theme.palette.mode === 'dark' 
        ? '1px solid #2a3c55' 
        : '1px solid #d1d5db',
    }}>
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          overflowX: twoDayView ? 'auto' : 'hidden',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorX: 'contain',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns,
            width: twoDayView ? 'max-content' : '100%',
            minWidth: twoDayView ? 'max-content' : '100%',
            gap: 0,
          }}
        >
          <Box
            sx={{
              position: twoDayView ? 'sticky' : 'static',
              left: 0,
              zIndex: 8,
              isolation: 'isolate',
              width: '100%',
              boxShadow: twoDayView ? '6px 0 10px rgba(0,0,0,0.22)' : 'none',
              background: (theme) => theme.palette.mode === 'dark' ? '#0d1520' : '#f0f4f8',
              color: 'text.secondary',
              borderBottom: (theme) => theme.palette.mode === 'dark' 
                ? '1px solid #213247' 
                : '1px solid #d1d5db',
              padding: '6px 8px',
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
          >
            Time
          </Box>
        
        {/* Day Headers */}
        {DAYS.map(day => (
          <Box
            key={day}
            sx={{
              background: (theme) => theme.palette.mode === 'dark' ? '#0d1520' : '#f0f4f8',
              color: (theme) => theme.palette.mode === 'dark' ? '#9fb3c8' : '#111827',
              borderBottom: (theme) => theme.palette.mode === 'dark' 
                ? '1px solid #213247' 
                : '1px solid #d1d5db',
              padding: '6px 8px',
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
          >
            {DAY_LABELS[day as keyof typeof DAY_LABELS]}
          </Box>
        ))}
        
        {/* Time Column */}
        <Box
          sx={{
            position: twoDayView ? 'sticky' : 'static',
            left: 0,
            zIndex: 7,
            isolation: 'isolate',
            width: '100%',
            height: '100%',
            boxShadow: twoDayView ? '6px 0 10px rgba(0,0,0,0.22)' : 'none',
            background: (theme) => theme.palette.mode === 'dark' ? '#0f1722' : '#f0f4f8',
          }}
        >
          {renderTimeColumn()}
        </Box>
        
        {/* Day Columns */}
        {DAYS.map(day => renderDayColumn(day))}
        </Box>
      </Box>
      {twoDayView && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '11px',
            padding: '6px 8px 4px',
            borderTop: (theme) => theme.palette.mode === 'dark'
              ? '1px solid #2a3c55'
              : '1px solid #e5e7eb',
          }}
        >
          Swipe to see the rest of the week
        </Typography>
      )}
    </Box>
  );
};

export default ScheduleGrid;
